import React from 'react'
import styled, {css} from 'styled-components'
import {Skeleton, Tooltip} from '../BaseComponents'
import {VisuallyHidden} from '../Primitives'
import {InfoHint} from './InfoHint'
import {EMPTY_VALUE, isBlankNode} from './internal/StatValue'

/**
 * DeltaIndicator
 *
 * A figure's change against a comparison ('▲ +12 placed', '▼ −0.4 pts', '20 min faster'). The
 * arrow shows which way the number moved; the colour shows whether that is good, so a fall in a
 * no-show rate can be green. The two are separate props for that reason.
 *
 * - `appearance="text"` (default) is a one-line caption under a figure, truncated with an ellipsis.
 *   `appearance="chip"` is the same content in a tinted pill. Cut off, either shows the whole
 *   change in the DS truncation tooltip, and only then (STFFLG-4236).
 * - `comparison` names what the change is against ('vs prior 12 weeks') when the visible text
 *   leaves it out: it goes in a `title` with the change ('▲ +12 placed vs prior 12 weeks'), not in
 *   the text. Without one there is no `title`: it would only repeat the text.
 * - `srText` replaces what a screen reader hears ('Down 0.4 points from the previous 12 weeks —
 *   better'); the arrow is never read. Say the comparison there too.
 * - `unavailableReason` is for a change that can't be computed: the slot reads '—', and the '—' is
 *   an InfoHint trigger carrying the reason. The slot's `data-testid` then sits on that trigger.
 * - `loading` holds the slot, test id included, with a skeleton hidden from assistive tech (the
 *   grid around it carries aria-busy). It wins over everything else.
 *
 * `DeltaIndicatorSpacer` is an empty text-appearance line, for a figure with no delta whose
 * neighbours have one, so the cells keep one height.
 *
 * @example
 *   <DeltaIndicator text="+12 placed" direction="up" sentiment="positive"
 *     comparison="vs prior 12 weeks" srText="Up 12 placements from the previous 12 weeks"
 *     data-testid={`${cellTestId}-delta`} />
 *   <DeltaIndicator text="−0.4 pts" direction="down" sentiment="positive" />
 *   <DeltaIndicator unavailableReason="Can’t compare yet — history starts 4 Jun 2026" />
 */

/** Which way the arrow points. 'neutral' draws no arrow. */
export type DeltaDirection = 'up' | 'down' | 'neutral'
/** Whether the change is good news. It sets the colour; the arrow never does. */
export type DeltaSentiment = 'positive' | 'negative' | 'neutral'
export type DeltaAppearance = 'text' | 'chip'

export interface DeltaIndicatorProps {
  /** What follows the arrow, e.g. '+12 placed' or '+2.1 pts vs prior 12 weeks'. */
  text?: string
  /**
   * What the change is measured against, when `text` doesn't say, e.g. 'vs prior 12 weeks'. Sets a
   * `title` of the change and it ('▲ +12 placed vs prior 12 weeks'); the visible text is unchanged.
   */
  comparison?: string
  /** ▲ for up, ▼ for down, nothing for neutral (the default). Hidden from assistive tech. */
  direction?: DeltaDirection
  /** positive = success ink, negative = error ink, neutral (the default) = secondary ink. */
  sentiment?: DeltaSentiment
  /** The whole change in words, read in place of the visible text. */
  srText?: string
  /**
   * The change can't be computed: the slot reads '—' as an InfoHint trigger with this reason, e.g.
   * 'Can’t compare yet — history starts 4 Jun 2026'. Wins over `text`.
   */
  unavailableReason?: React.ReactNode
  /** The '—' trigger's accessible name (default "No comparison"). */
  unavailableLabel?: string
  appearance?: DeltaAppearance
  /** The change is still loading: a skeleton line in the slot. Wins over everything else. */
  loading?: boolean
  /** On the delta, or on the '—' trigger when the change is unavailable. Matrix `…-delta`. */
  'data-testid'?: string
}

const GLYPH: Record<DeltaDirection, string> = {up: '▲', down: '▼', neutral: ''}

/** Small status text is --*-800 (CLAUDE.md §3a). */
const INK: Record<DeltaSentiment, string> = {
  positive: 'var(--success-800)',
  negative: 'var(--error-800)',
  neutral: 'var(--text-secondary)',
}

/**
 * The chip's fill. Neutral has none, with a hairline instead: secondary ink on the lighter
 * surfaces is only just 4.5:1, so it keeps the surface it sits on.
 */
const CHIP_FILL: Record<DeltaSentiment, string> = {
  positive: 'var(--success-100)',
  negative: 'var(--error-100)',
  neutral: 'transparent',
}

const CHIP_EDGE: Record<DeltaSentiment, string> = {
  positive: 'transparent',
  negative: 'transparent',
  neutral: 'var(--border-subtle)',
}

const typeScale = css`
  font-family: var(--font-sans);
  font-size: var(--fs-caption);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

/** The text appearance's line, and the spacer's. No overflow clip: the '—' trigger's target and
 *  focus ring reach past it. */
const Line = styled.span<{$sentiment: DeltaSentiment}>`
  ${typeScale}
  display: block;
  max-width: 100%;
  font-weight: var(--fw-regular);
  line-height: var(--lh-normal);
  color: ${({$sentiment}) => INK[$sentiment]};
`

/** A computed change on one line, cut with an ellipsis (the truncation tooltip shows it whole). */
const TextValue = styled(Line)`
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Chip = styled.span<{$sentiment: DeltaSentiment}>`
  ${typeScale}
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  max-width: 100%;
  box-sizing: border-box;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-pill);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-tight);
  color: ${({$sentiment}) => INK[$sentiment]};
  background: ${({$sentiment}) => CHIP_FILL[$sentiment]};
  box-shadow: inset 0 0 0 var(--border-thin) ${({$sentiment}) => CHIP_EDGE[$sentiment]};
`

/** A short bar inline in the line (or chip), so a loading slot is as tall as a loaded one. */
const LoadingBar = styled(Skeleton)`
  display: inline-block;
  vertical-align: middle;
`

/** Inside the chip, the words take the ellipsis; the arrow stays whole. */
const ChipText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`

export function DeltaIndicator({
  text,
  comparison,
  direction = 'neutral',
  sentiment = 'neutral',
  srText,
  unavailableReason,
  unavailableLabel = 'No comparison',
  appearance = 'text',
  loading = false,
  'data-testid': testId,
}: DeltaIndicatorProps) {
  if (loading) {
    return appearance === 'chip' ? (
      <Chip $sentiment="neutral" aria-hidden="true" data-testid={testId}>
        <LoadingBar width="3em" height="1em" />
      </Chip>
    ) : (
      <Line $sentiment="neutral" aria-hidden="true" data-testid={testId}>
        <LoadingBar width="5em" height="1em" />
      </Line>
    )
  }

  const unavailable = !isBlankNode(unavailableReason)
  if (unavailable || !text) {
    // '—' with its reason, or a bare '—' when there is neither a change nor a reason.
    const dash = unavailable ? (
      <InfoHint label={unavailableLabel} content={unavailableReason} data-testid={testId}>
        {EMPTY_VALUE}
      </InfoHint>
    ) : (
      EMPTY_VALUE
    )
    const testIdOnSlot = unavailable ? undefined : testId
    return appearance === 'chip' ? (
      <Chip $sentiment="neutral" data-testid={testIdOnSlot}>
        {dash}
      </Chip>
    ) : (
      <Line $sentiment="neutral" data-testid={testIdOnSlot}>
        {dash}
      </Line>
    )
  }

  const glyph = GLYPH[direction]
  const change = [glyph, text].filter(Boolean).join(' ')
  // The comparison is said nowhere else, so it is the `title`. Without one, a title would only
  // repeat the line over text the eye can read (STFFLG-4236).
  const title = comparison ? `${change} ${comparison}` : undefined
  // With srText the visible words are hidden and the sentence is read instead, once.
  const words = srText ? {'aria-hidden': true as const} : {}
  const arrow = glyph ? <span aria-hidden="true">{glyph}</span> : null
  const spoken = srText ? <VisuallyHidden>{srText}</VisuallyHidden> : null

  const delta =
    appearance === 'chip' ? (
      <Chip $sentiment={sentiment} title={title} data-testid={testId}>
        {arrow}
        {glyph ? ' ' : null}
        <ChipText {...words}>{text}</ChipText>
        {spoken}
      </Chip>
    ) : (
      <TextValue $sentiment={sentiment} title={title} data-testid={testId}>
        {arrow}
        {glyph ? ' ' : null}
        <span {...words}>{text}</span>
        {spoken}
      </TextValue>
    )
  if (comparison) return delta
  // A copy of the change for when it is cut off with "…": it shows only then, and adds no
  // description (the words, or srText, are read already). A block trigger, so the chip's
  // `max-width: 100%` still measures the slot it sits in, and it still truncates.
  return (
    <Tooltip content={change} block truncatedOnly describeChild={false}>
      {delta}
    </Tooltip>
  )
}

/** An empty delta line, hidden from assistive tech: keeps a figure without a delta level with
 *  its neighbours. It has no test id; a missing delta is not a delta slot. */
export function DeltaIndicatorSpacer() {
  return (
    <Line $sentiment="neutral" aria-hidden="true">
      {'\u00a0'}
    </Line>
  )
}
